/**
 * Composant EmojiPicker - Sélecteur d'émojis pour les messages
 * 
 * Affiche une grille d'émojis organisés par catégories.
 * Utilisé pour le chat global et les conversations privées.
 */

import React, { useState, useRef, useEffect } from 'react';
import '../styles/emoji-picker.css';

interface EmojiPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (emoji: string) => void;
  anchorRef?: React.RefObject<HTMLElement | null>;
  closeOnSelect?: boolean;
  variant?: 'input' | 'reaction';
}

// Catégories d'émojis populaires
const EMOJI_CATEGORIES = [
  {
    id: 'smileys',
    name: 'Smileys',
    icon: '😊',
    emojis: [
      '😊', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '😉',
      '😌', '😍', '🥰', '😘', '😗', '😚', '😋', '😛', '😜', '🤪',
      '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑',
      '😶', '😏', '😒', '🙄', '😬', '😮‍💨', '🤥', '😌', '😔', '😪',
      '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶',
      '🥴', '😵', '😵‍💫', '🤯', '🤠', '🥳', '🥸', '😎', '🤓', '🧐',
      '😕', '😟', '🙁', '☹️', '😮', '😯', '😲', '😳', '🥺', '😦',
      '😧', '😨', '😰', '😥', '😢', '😭', '😱', '😖', '😣', '😞',
      '😓', '😩', '😫', '🥱', '😤', '😡', '😠', '🤬', '😈', '👿',
    ],
  },
  {
    id: 'gestures',
    name: 'Gestes',
    icon: '👍',
    emojis: [
      '👍', '👎', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙',
      '👈', '👉', '👆', '👇', '☝️', '✋', '🤚', '🖐️', '🖖', '👋',
      '🤝', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✍️', '💪', '🦾',
      '🤳', '💅', '🖕', '✊', '👊', '🤛', '🤜', '🫶', '🫰', '🫵',
    ],
  },
  {
    id: 'hearts',
    name: 'Cœurs',
    icon: '❤️',
    emojis: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
      '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '♥️',
      '😍', '🥰', '😘', '😻', '💑', '💏', '👩‍❤️‍👨', '💋', '💌', '🫀',
    ],
  },
  {
    id: 'fun',
    name: 'Fun',
    icon: '🎉',
    emojis: [
      '🎉', '🎊', '🎈', '🎁', '🎀', '🏆', '🥇', '🥈', '🥉', '🏅',
      '🎯', '🎮', '🎲', '🎭', '🎨', '🎬', '🎤', '🎧', '🎵', '🎶',
      '🌟', '⭐', '✨', '💫', '🔥', '💯', '💥', '💢', '💨', '💦',
      '🍀', '🌈', '☀️', '🌙', '⚡', '❄️', '🎄', '🎃', '👻', '💀',
    ],
  },
  {
    id: 'food',
    name: 'Nourriture',
    icon: '🍕',
    emojis: [
      '🍕', '🍔', '🍟', '🌭', '🍿', '🧂', '🥓', '🥚', '🍳', '🧇',
      '🥞', '🧈', '🍞', '🥐', '🥖', '🥨', '🧀', '🥗', '🥙', '🌮',
      '🌯', '🥪', '🍝', '🍜', '🍲', '🍛', '🍣', '🍱', '🥟', '🦪',
      '🍦', '🍧', '🍨', '🍩', '🍪', '🎂', '🍰', '🧁', '🥧', '🍫',
      '🍬', '🍭', '☕', '🍵', '🧃', '🥤', '🍶', '🍺', '🍻', '🥂',
      '🍷', '🥃', '🍸', '🍹', '🧉', '🍾', '🧊', '🥛', '🍼', '🫖',
    ],
  },
  {
    id: 'animals',
    name: 'Animaux',
    icon: '🐶',
    emojis: [
      '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐻‍❄️', '🐨',
      '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🐔', '🐧', '🐦', '🐤',
      '🦆', '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛',
      '🦋', '🐌', '🐞', '🐜', '🦟', '🦗', '🐢', '🐍', '🦎', '🦂',
      '🐠', '🐟', '🐡', '🐬', '🦈', '🐳', '🐋', '🐊', '🐆', '🦓',
    ],
  },
  {
    id: 'objects',
    name: 'Objets',
    icon: '💡',
    emojis: [
      '💡', '🔦', '🕯️', '💎', '💰', '💳', '💵', '💴', '💶', '💷',
      '🔑', '🗝️', '🔒', '🔓', '🔐', '📱', '💻', '🖥️', '⌨️', '🖱️',
      '📷', '📸', '📹', '🎥', '📺', '📻', '🎙️', '🎚️', '⏰', '⏱️',
      '📞', '☎️', '📟', '📠', '📧', '✉️', '📨', '📩', '📪', '📫',
      '📦', '🏷️', '📝', '📚', '📖', '🔖', '📌', '📍', '✂️', '📎',
    ],
  },
  {
    id: 'symbols',
    name: 'Symboles',
    icon: '✅',
    emojis: [
      '✅', '❌', '❓', '❗', '‼️', '⁉️', '💤', '💬', '💭', '🗯️',
      '♠️', '♣️', '♥️', '♦️', '🃏', '🎴', '🀄', '🔇', '🔈', '🔉',
      '🔊', '📣', '📢', '🔔', '🔕', '🎼', '🎵', '🎶', '➕', '➖',
      '➗', '✖️', '♾️', '💲', '©️', '®️', '™️', '🔴', '🟠', '🟡',
      '🟢', '🔵', '🟣', '⚫', '⚪', '🟤', '⬛', '⬜', '🔶', '🔷',
    ],
  },
  {
    id: 'tarot',
    name: 'Tarot',
    icon: '🃏',
    emojis: [
      '🃏', '🎴', '♠️', '♣️', '♥️', '♦️', '👑', '🤴', '👸', '🏰',
      '⚔️', '🛡️', '🎯', '🏆', '🥇', '🥈', '🥉', '🎖️', '🏅', '🌟',
      '⭐', '💫', '✨', '🔮', '🎲', '🎰', '🎪', '🎭', '🎨', '🎬',
    ],
  },
];

export default function EmojiPicker({
  isOpen,
  onClose,
  onSelect,
  anchorRef,
  closeOnSelect = false,
  variant = 'input',
}: EmojiPickerProps) {
  const [activeCategory, setActiveCategory] = useState('smileys');
  const pickerRef = useRef<HTMLDivElement>(null);

  // Fermer quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node) &&
        (!anchorRef?.current || !anchorRef.current.contains(event.target as Node))
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  // Fermer avec Escape
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const currentEmojis = EMOJI_CATEGORIES.find(cat => cat.id === activeCategory)?.emojis || [];

  const handleEmojiClick = (emoji: string) => {
    onSelect(emoji);
    if (closeOnSelect) {
      onClose();
    }
  };

  return (
    <div
      className={`emoji-picker ${variant === 'reaction' ? 'emoji-picker--reaction' : ''}`.trim()}
      ref={pickerRef}
    >
      {/* Bouton fermer */}
      <button className="emoji-close-button" onClick={onClose} title="Fermer">
        ×
      </button>

      {/* Navigation par catégories */}
      <div className="emoji-categories">
        {EMOJI_CATEGORIES.map(category => (
          <button
            key={category.id}
            className={`emoji-category-button ${activeCategory === category.id ? 'active' : ''}`}
            onClick={() => setActiveCategory(category.id)}
            title={category.name}
          >
            {category.icon}
          </button>
        ))}
      </div>

      {/* Grille d'emojis */}
      <div className="emoji-grid">
        {currentEmojis.map((emoji, index) => (
          <button
            key={`${emoji}-${index}`}
            className="emoji-button"
            onClick={() => handleEmojiClick(emoji)}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
}
